import React, { useState, useRef, useEffect } from 'react';
import './ActionMenu.css';

/**
 * Menu d'actions générique : bouton déclencheur + dropdown.
 *
 * Props :
 *  - items   : [{ label, icon?, onClick?, href?, download?, danger? }]
 *              href => rendu en <a> (ex: téléchargement), sinon <button>
 *  - label   : texte du bouton déclencheur (optionnel — sans label : kebab ⋮)
 *  - variant : 'icon' (défaut, bouton ⋮ discret) | 'button' (style btn-primary)
 *  - align   : 'right' (défaut) | 'left' — alignement du dropdown
 */
export default function ActionMenu({ items = [], label, variant = 'icon', align = 'right' }) {
  const [open, setOpen] = useState(false);
  // Position fixe (viewport) calculée à l'ouverture : le dropdown flotte
  // au-dessus de tout, même dans un conteneur overflow (ex: .cf-table-wrapper)
  // où un dropdown absolu créerait un scrollbar et pousserait le contenu.
  const [pos, setPos] = useState({ top: 0, left: 0, right: 0 });
  const ref = useRef(null);

  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    // capture: true pour attraper aussi les scrolls des conteneurs internes
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const kebabIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
  );
  const chevron = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );

  return (
    <div className="action-menu" ref={ref}>
      <button
        type="button"
        className={variant === 'button' ? 'btn btn-primary btn-sm action-menu-trigger-btn' : 'action-menu-trigger'}
        title={label || 'Actions'}
        onClick={toggle}
      >
        {label ? (<>{label} {chevron}</>) : kebabIcon}
      </button>
      {open && (
        <div
          className="action-menu-dropdown"
          style={align === 'left'
            ? { top: pos.top, left: pos.left }
            : { top: pos.top, right: pos.right }}
        >
          {items.map((item, i) => {
            const content = (
              <>
                {item.icon && <span className="action-menu-item-icon">{item.icon}</span>}
                {item.label}
              </>
            );
            if (item.href) {
              return (
                <a
                  key={i}
                  href={item.href}
                  download={item.download}
                  className={`action-menu-item ${item.danger ? 'danger' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  {content}
                </a>
              );
            }
            return (
              <button
                key={i}
                type="button"
                className={`action-menu-item ${item.danger ? 'danger' : ''}`}
                onClick={() => { setOpen(false); if (item.onClick) item.onClick(); }}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
